import type { ChartPoint } from '@/admin/dashboards/chartViewModel';

/** The tallest still-revising bar fills at most this share of the plot, so bars stay under the score band. */
export const REVISING_BAR_MAX_PLOT_SHARE = 0.6;

const NICE_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000];
const MAX_TICK_INTERVALS = 3;

function revisingCounts(points: ChartPoint[] | null | undefined): number[] {
  return (points ?? [])
    .map((point) => point.nRevising)
    .filter((count): count is number => typeof count === 'number');
}

export function hasRevisingCounts(points: ChartPoint[] | null | undefined): boolean {
  return revisingCounts(points).length > 0;
}

/**
 * Right-hand count axis for the still-revising bars. Ticks stop at a round number just above the
 * largest count; the domain runs higher so the bars never climb into the score band.
 */
export function revisingCountAxis(
  points: ChartPoint[] | null | undefined,
): { domain: [number, number]; ticks: number[] } | null {
  const counts = revisingCounts(points);
  if (!counts.length) return null;
  const largest = Math.max(...counts);
  if (largest <= 0) return null;
  const step = NICE_STEPS.find((candidate) => Math.ceil(largest / candidate) <= MAX_TICK_INTERVALS)
    ?? NICE_STEPS[NICE_STEPS.length - 1];
  const top = Math.ceil(largest / step) * step;
  const ticks: number[] = [];
  for (let tick = 0; tick <= top; tick += step) ticks.push(tick);
  return { domain: [0, top / REVISING_BAR_MAX_PLOT_SHARE], ticks };
}
